import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { isNil } from 'lodash';
import moment from 'moment-timezone';
import useCurrentDate from 'hooks/useCurrentDate';
import { isAdminNetworkAccountType } from 'modules/account-management/accountType';
import { AllEntityId } from 'modules/campaigns/config/allEntityId';
import { useSelectedCampaign } from 'modules/campaigns/hooks/useSelectedCampaign';
import { useSelectedCampaignGroup } from 'modules/campaigns/hooks/useSelectedCampaignGroup';
import { FEATURE_FLAGS, useConfigMatch } from 'modules/taboola-common-frontend-modules/account-configurations';
import { useIntl } from 'modules/taboola-common-frontend-modules/i18n';
import { selectedAccountSelector } from '../../../../../selectors';
import { isStaticReport } from '../../../config/isStaticDynamicReport';
import { DYNAMIC_PRESETS, DYNAMIC_PRESET_LABELS } from '../config/dynamicPresets';
import { getPresetLabel, createPreset } from '../utils/dashboardRangePickerUtils';
import { useSelectedReportId } from './useSelectedReportId';

const useLifetimePreset = ({ timeZoneName }) => {
    const { formatMessage } = useIntl();
    const [reportId] = useSelectedReportId();
    const label = getPresetLabel(formatMessage, DYNAMIC_PRESETS.LIFETIME, DYNAMIC_PRESET_LABELS.LIFETIME);

    const currentDate = useCurrentDate({ timeZoneName });

    const { campaign, campaignId: selectedCampaignId } = useSelectedCampaign();
    const { startDate: selectedCampaignStartDate, endDate: selectedCampaignEndDate } = campaign || {};
    const { campaignGroup, campaignGroupId: selectedCampaignGroupId } = useSelectedCampaignGroup();
    const { startDate: selectedCampaignGroupStartDate, endDate: selectedCampaignGroupEndDate } = campaignGroup || {};

    const {
        id: selectedAccountId,
        firstCampaignStartDate: accountFirstCampaignStartDate,
        type: selectedAccountType,
    } = useSelector(selectedAccountSelector);

    const isLifetimePresetEnabled =
        useConfigMatch({
            [FEATURE_FLAGS.LIFETIME_RANGE_PICKER_PRESET_ENABLED]: 'true',
        }) &&
        !isAdminNetworkAccountType(selectedAccountType) &&
        isStaticReport(reportId);

    const isSpecificCampaignSelected = selectedCampaignId !== AllEntityId;
    const isSpecificCampaignGroupSelected = selectedCampaignGroupId !== AllEntityId;
    const isSpecificEntitySelected = isSpecificCampaignSelected || isSpecificCampaignGroupSelected;

    const presetForAccount = useMemo(
        () =>
            createPreset({
                preset: DYNAMIC_PRESETS.LIFETIME,
                label,
                startDate: moment(accountFirstCampaignStartDate).startOf('day'),
                endDate: currentDate,
            }),
        [label, accountFirstCampaignStartDate, currentDate]
    );

    const presetForCampaign = useMemo(
        () =>
            createPreset({
                preset: DYNAMIC_PRESETS.LIFETIME,
                label,
                startDate: moment(selectedCampaignStartDate).startOf('day'),
                endDate: selectedCampaignEndDate ? moment(selectedCampaignEndDate).startOf('day') : currentDate,
            }),
        [currentDate, label, selectedCampaignEndDate, selectedCampaignStartDate]
    );
    const presetForCampaignGroup = useMemo(
        () =>
            createPreset({
                preset: DYNAMIC_PRESETS.LIFETIME,
                label,
                startDate: moment(selectedCampaignGroupStartDate).startOf('day'),
                endDate: selectedCampaignGroupEndDate
                    ? moment(selectedCampaignGroupEndDate).startOf('day')
                    : currentDate,
            }),
        [currentDate, label, selectedCampaignGroupEndDate, selectedCampaignGroupStartDate]
    );

    const placeholderPreset = useMemo(
        () =>
            createPreset({
                preset: DYNAMIC_PRESETS.LIFETIME,
                label,
                startDate: currentDate,
                endDate: currentDate,
            }),
        [currentDate, label]
    );

    const lifetimePreset = useMemo(() => {
        if (
            !isLifetimePresetEnabled ||
            (!isSpecificEntitySelected && accountFirstCampaignStartDate === null) || //user on account level but no data for start date of first campaign
            (isSpecificCampaignSelected &&
                selectedCampaignStartDate &&
                moment(selectedCampaignStartDate).startOf('day').isAfter(currentDate)) || //campaign start date in the future
            (isSpecificCampaignGroupSelected &&
                selectedCampaignGroupStartDate &&
                moment(selectedCampaignGroupStartDate).startOf('day').isAfter(currentDate)) //campaign group start date in the future
        ) {
            return {};
        }
        if (!isSpecificEntitySelected && !isNil(accountFirstCampaignStartDate)) {
            return presetForAccount;
        }
        if (isSpecificCampaignSelected && !isNil(selectedCampaignStartDate)) {
            return presetForCampaign;
        }
        if (isSpecificCampaignGroupSelected && !isNil(selectedCampaignGroupStartDate)) {
            return presetForCampaignGroup;
        }
        //We must provide placeholder preset so app doesn't
        //use default time range when the network call that populates the
        //selectedCampaignStartDate or firstCampaignStartDate fields in
        //redux hasn't responded yet
        return placeholderPreset;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isLifetimePresetEnabled,
        selectedCampaignId,
        accountFirstCampaignStartDate,
        isSpecificCampaignSelected,
        selectedCampaignStartDate,
        placeholderPreset,
        presetForAccount,
        presetForCampaign,
        selectedAccountId,
        selectedCampaignGroupId,
        selectedCampaignGroupStartDate,
        isSpecificCampaignGroupSelected,
        isSpecificEntitySelected,
    ]);

    return lifetimePreset;
};

export default useLifetimePreset;
